import React, { useState } from 'react';
import axios from 'axios';
import {
    FiUpload,
    FiFileText,
    FiCheckCircle,
    FiAlertCircle,
    FiX,
    FiPaperclip,
} from 'react-icons/fi';
import './CertificateUpload.css';

const api = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

/* ── All valid certificate types ── */
const CERTIFICATE_TYPES = [
    { value: '', label: '— Select Type —' },
    { value: 'TENTH_CERTIFICATE', label: '10th Certificate' },
    { value: 'TWELFTH_CERTIFICATE', label: '12th Certificate' },
    { value: 'DIPLOMA_CERTIFICATE', label: 'Diploma Certificate' },
    { value: 'DEGREE_CERTIFICATE', label: 'Degree Certificate' },
    { value: 'POST_GRADUATION_CERTIFICATE', label: 'Post Graduation Certificate' },
    { value: 'PHD_CERTIFICATE', label: 'PhD Certificate' },
    { value: 'EXPERIENCE_CERTIFICATE', label: 'Experience Certificate' },
    { value: 'RELIEVING_LETTER', label: 'Relieving Letter' },
    { value: 'SALARY_CERTIFICATE', label: 'Salary Certificate' },
    { value: 'TECHNICAL_CERTIFICATION', label: 'Technical Certification' },
    { value: 'PROFESSIONAL_CERTIFICATION', label: 'Professional Certification' },
    { value: 'TRAINING_CERTIFICATE', label: 'Training Certificate' },
    { value: 'AADHAAR_CARD', label: 'Aadhaar Card' },
    { value: 'PAN_CARD', label: 'PAN Card' },
    { value: 'PASSPORT', label: 'Passport' },
    { value: 'DRIVING_LICENSE', label: 'Driving License' },
    { value: 'VOTER_ID', label: 'Voter ID' },
    { value: 'MEDICAL_CERTIFICATE', label: 'Medical Certificate' },
    { value: 'CHARACTER_CERTIFICATE', label: 'Character Certificate' },
    { value: 'CASTE_CERTIFICATE', label: 'Caste Certificate' },
    { value: 'INCOME_CERTIFICATE', label: 'Income Certificate' },
    { value: 'RESIDENCE_CERTIFICATE', label: 'Residence Certificate' },
    { value: 'OTHER', label: 'Other' },
];

const EMPTY_FORM = {
    certificateName: '',
    certificateType: '',
    issuingAuthority: '',
    issueDate: '',
    expiryDate: '',
    remarks: '',
};

const CertificateUpload = () => {
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState(null); // { type, msg }

    const showToast = (type, msg) => {
        setToast({ type, msg });
        setTimeout(() => setToast(null), 4000);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0] || null);
    };

    const handleFileDrop = (e) => {
        e.preventDefault();
        const dropped = e.dataTransfer.files[0];
        if (dropped) setFile(dropped);
    };

    const handleRemoveFile = () => setFile(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.certificateName.trim()) {
            showToast('error', 'Certificate Name is required.');
            return;
        }
        if (!formData.certificateType) {
            showToast('error', 'Please select a Certificate Type.');
            return;
        }

        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append('certificateName', formData.certificateName);
            fd.append('certificateType', formData.certificateType);
            if (formData.issuingAuthority) fd.append('issuingAuthority', formData.issuingAuthority);
            if (formData.issueDate) fd.append('issueDate', formData.issueDate);
            if (formData.expiryDate) fd.append('expiryDate', formData.expiryDate);
            if (formData.remarks) fd.append('remarks', formData.remarks);
            if (file) fd.append('file', file);

            await api.post('/certificate/upload', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            showToast('success', 'Certificate uploaded successfully!');
            setFormData(EMPTY_FORM);
            setFile(null);
        } catch (err) {
            console.error(err);
            const msg = err?.response?.data;
            showToast('error', typeof msg === 'string' ? msg : 'Upload failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReset = () => {
        setFormData(EMPTY_FORM);
        setFile(null);
    };

    return (
        <div className="dashboard-layout">

            <div className="main-content">
                <div className="cert-wrapper">

                    {/* ── Toast ── */}
                    {toast && (
                        <div className={`cert-toast cert-toast--${toast.type}`}>
                            {toast.type === 'success' ? <FiCheckCircle size={16} /> : <FiAlertCircle size={16} />}
                            <span>{toast.msg}</span>
                            <button className="cert-toast-close" onClick={() => setToast(null)}>
                                <FiX size={14} />
                            </button>
                        </div>
                    )}

                    {/* ── Page header ── */}
                    <div className="cert-header">
                        <div className="cert-header-icon"><FiFileText size={22} /></div>
                        <div>
                            <h2 className="cert-title">Upload Certificate</h2>
                            <p className="cert-subtitle">Upload your certificates and documents securely</p>
                        </div>
                    </div>

                    {/* ── Form card ── */}
                    <div className="cert-card">
                        <form onSubmit={handleSubmit} noValidate>

                            {/* ── Required fields ── */}
                            <div className="cert-section-title">Certificate Details</div>
                            <div className="cert-grid">

                                <div className="cert-field cert-field--full">
                                    <label>Certificate Name <span className="cert-required">*</span></label>
                                    <input
                                        type="text"
                                        name="certificateName"
                                        placeholder="e.g. B.Tech Degree Certificate"
                                        value={formData.certificateName}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="cert-field">
                                    <label>Certificate Type <span className="cert-required">*</span></label>
                                    <select
                                        name="certificateType"
                                        value={formData.certificateType}
                                        onChange={handleChange}
                                    >
                                        {CERTIFICATE_TYPES.map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="cert-field">
                                    <label>Issuing Authority</label>
                                    <input
                                        type="text"
                                        name="issuingAuthority"
                                        placeholder="e.g. JNTU Hyderabad"
                                        value={formData.issuingAuthority}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="cert-field">
                                    <label>Issue Date</label>
                                    <input
                                        type="date"
                                        name="issueDate"
                                        value={formData.issueDate}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="cert-field">
                                    <label>Expiry Date</label>
                                    <input
                                        type="date"
                                        name="expiryDate"
                                        value={formData.expiryDate}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="cert-field cert-field--full">
                                    <label>Remarks</label>
                                    <textarea
                                        name="remarks"
                                        placeholder="Any additional notes..."
                                        rows={3}
                                        value={formData.remarks}
                                        onChange={handleChange}
                                    />
                                </div>

                            </div>

                            {/* ── File upload zone ── */}
                            <div className="cert-section-title" style={{ marginTop: 24 }}>Attach File</div>
                            <div
                                className={`cert-dropzone${file ? ' cert-dropzone--has-file' : ''}`}
                                onDragOver={e => e.preventDefault()}
                                onDrop={handleFileDrop}
                                onClick={() => !file && document.getElementById('cert-file-input').click()}
                            >
                                {file ? (
                                    <div className="cert-file-info">
                                        <FiPaperclip size={20} />
                                        <span className="cert-file-name">{file.name}</span>
                                        <span className="cert-file-size">
                                            ({(file.size / 1024).toFixed(1)} KB)
                                        </span>
                                        <button
                                            type="button"
                                            className="cert-file-remove"
                                            onClick={e => { e.stopPropagation(); handleRemoveFile(); }}
                                        >
                                            <FiX size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="cert-dropzone-empty">
                                        <FiUpload size={28} />
                                        <p>Drag &amp; drop a file here, or <span className="cert-browse">browse</span></p>
                                        <p className="cert-dropzone-hint">PDF, JPG, PNG, DOC — max 10 MB</p>
                                    </div>
                                )}
                            </div>
                            <input
                                id="cert-file-input"
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />

                            {/* ── Actions ── */}
                            <div className="cert-actions">
                                <button
                                    type="button"
                                    className="cert-btn-reset"
                                    onClick={handleReset}
                                    disabled={submitting}
                                >
                                    Reset
                                </button>
                                <button
                                    type="submit"
                                    className="cert-btn-submit"
                                    disabled={submitting}
                                >
                                    {submitting ? 'Uploading…' : <><FiUpload size={15} /> Upload Certificate</>}
                                </button>
                            </div>

                        </form>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default CertificateUpload;